import React from 'react';
import {Show, useRecordContext, useTranslate} from 'react-admin';
import {Link as RouterLink} from 'react-router-dom';
import {Avatar, Box, Typography} from '@mui/material';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import SectionCard from '../components/statamic/SectionCard';
import DataRow from '../components/statamic/DataRow';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';

const formatDateTime = (v) => {
    if (!v) return null;
    const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const RecipientLink = ({user}) => {
    if (!user) return null;
    const avatarUrl = user.avatar ? `${process.env.REACT_APP_CDN_URL}/${user.avatar}` : undefined;
    const fullName = [user.given_name, user.family_name].filter(Boolean).join(' ') || user.email || 'Unnamed';

    return (
        <Box
            component={RouterLink}
            to={`/guardians/${user.id}/show`}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.25,
                textDecoration: 'none',
                color: 'inherit',
                p: 0.5,
                borderRadius: `${t.radius.sm}px`,
                '&:hover': {
                    backgroundColor: t.color.sidebarHover,
                },
            }}
        >
            <Avatar
                src={avatarUrl}
                sx={{
                    width: 36, height: 36,
                    bgcolor: t.color.sidebarHover,
                    color: t.color.text.secondary,
                    border: `1px solid ${t.color.border.hairline}`,
                    fontSize: 13,
                }}
            >
                {(user.given_name?.charAt(0) ?? '') + (user.family_name?.charAt(0) ?? '')}
            </Avatar>
            <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.accent.primary, fontWeight: 500}}>
                {fullName}
            </Typography>
        </Box>
    );
};

const ClickEvents = ({events, translate}) => {
    if (!events || events.length === 0) {
        return (
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted, fontStyle: 'italic'}}>
                No click events recorded.
            </Typography>
        );
    }
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
            {events.map((e, i) => (
                <Box
                    key={i}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: `${t.type.body.size}px`,
                        color: t.color.text.primary,
                    }}
                >
                    <StatusDot variant="info"/>
                    <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary}}>
                        {translate(e.eventType)}
                    </Typography>
                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                        {formatDateTime(e.eventDateTime)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

const MailLogsShowContent = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    const successPill = record.success === true || record.success === 'true'
        ? <StatusPill variant="success">Sent</StatusPill>
        : record.success === false || record.success === 'false'
            ? <StatusPill variant="danger">Failed</StatusPill>
            : null;

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={{label: translate('Mail Logs'), to: '/mailLogs'}}
                title={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={record.success ? 'success' : 'neutral'}/>
                        <Box component="span">{record.subject || record.mail_type || 'Mail log'}</Box>
                    </Box>
                }
            />

            <Box sx={{p: 3, display: 'flex', flexDirection: 'column', gap: 2}}>
                <Box
                    sx={{
                        backgroundColor: t.color.surface,
                        border: `1px solid ${t.color.border.default}`,
                        borderRadius: `${t.radius.md}px`,
                        p: 3,
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 4,
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 20,
                                fontWeight: 700,
                                color: t.color.text.primary,
                                lineHeight: 1.3,
                                wordBreak: 'break-word',
                            }}
                        >
                            {record.subject || '—'}
                        </Typography>
                        <Typography sx={{mt: 0.5, fontSize: `${t.type.small.size}px`, color: t.color.text.secondary}}>
                            {record.mail_type}
                        </Typography>
                        <Box sx={{display: 'flex', gap: 0.75, mt: 1.5, flexWrap: 'wrap'}}>
                            {successPill}
                            <StatusPill variant={record.read ? 'info' : 'neutral'}>
                                {record.read ? translate('Read') : 'Unread'}
                            </StatusPill>
                            {record.clicked && <StatusPill variant="info">{translate('Clicked')}</StatusPill>}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            pl: 3,
                            borderLeft: `1px solid ${t.color.border.hairline}`,
                            gap: 0.75,
                        }}
                    >
                        <DataRow label={translate('Date sent')} value={formatDateTime(record.sent_date_time)}/>
                        {record.read_date_time && (
                            <DataRow label="Read at" value={formatDateTime(record.read_date_time)}/>
                        )}
                        <DataRow label={translate('Language')} value={record.language_code}/>
                    </Box>
                </Box>

                <SectionCard title={translate('Recipient info')}>
                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0, alignItems: 'center'}}>
                        <DataRow label={translate('Guardian name')}>
                            {record.nofam_user
                                ? <RecipientLink user={record.nofam_user}/>
                                : record.recipient}
                        </DataRow>
                        <DataRow label={translate('Last access')} value={formatDateTime(record.last_access_date_time)}/>
                    </Box>
                </SectionCard>

                <SectionCard title={translate('Sender info')}>
                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 3, rowGap: 0}}>
                        <DataRow label={translate('Charity')} value={record.charity}/>
                        <DataRow label={translate('Sender')} value={record.sender}/>
                    </Box>
                </SectionCard>

                <SectionCard title={translate('Click events')}>
                    <ClickEvents events={record.mail_click_events} translate={translate}/>
                </SectionCard>
            </Box>
        </Box>
    );
};

const MailLogsShowStatamic = (props) => (
    <Show {...props} actions={false} component={Box} sx={{backgroundColor: t.color.page}}>
        <MailLogsShowContent/>
    </Show>
);

export default MailLogsShowStatamic;
